import React, { useState } from 'react';
import { User, Mail, Lock, Crown, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Layout/Navbar';
import './ProfilePage.css';

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const updateProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put('/auth/update-profile', { name });
      await refreshUser();
      toast.success('Profile updated');
    } catch { toast.error('Failed to update profile'); }
    finally { setSavingProfile(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) return toast.error('Passwords do not match');
    if (passwords.new.length < 6) return toast.error('Password must be at least 6 characters');
    setSavingPassword(true);
    try {
      await api.put('/auth/change-password', { currentPassword: passwords.current, newPassword: passwords.new });
      toast.success('Password changed successfully');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to change password'); }
    finally { setSavingPassword(false); }
  };

  return (
    <div className="profile-page">
      <Navbar />
      <div className="profile-body">
        <h1>Profile Settings</h1>

        <div className="profile-grid">
          <div className="card">
            <h2><User size={16} /> Personal Info</h2>
            <form onSubmit={updateProfile} className="profile-form">
              <label>Display Name</label>
              <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" />
              <label>Email</label>
              <input className="input" value={user?.email} disabled />
              <button className="btn btn-primary" type="submit" disabled={savingProfile}>
                {savingProfile ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>

          <div className="card">
            <h2><Crown size={16} /> Subscription</h2>
            <div className="sub-info">
              <div className="sub-row">
                <span>Current Plan</span>
                <span className={`badge badge-${user?.plan}`}>{user?.plan?.toUpperCase()}</span>
              </div>
              <div className="sub-row">
                <span>Status</span>
                <span style={{ color: user?.isPremium ? 'var(--accent)' : 'var(--text-muted)' }}>
                  {user?.isPremium ? 'Active' : 'Free Tier'}
                </span>
              </div>
              {user?.subscriptionExpiry && (
                <div className="sub-row">
                  <span>Expires</span>
                  <span>{new Date(user.subscriptionExpiry).toLocaleDateString()}</span>
                </div>
              )}
              <div className="sub-row">
                <span>Uploads this month</span>
                <span>{user?.uploadsThisMonth || 0}</span>
              </div>
              <div className="sub-row">
                <span>AI requests this month</span>
                <span>{user?.aiRequestsThisMonth || 0}</span>
              </div>
            </div>
          </div>

          <div className="card">
            <h2><Lock size={16} /> Change Password</h2>
            <form onSubmit={changePassword} className="profile-form">
              <label>Current Password</label>
              <input className="input" type="password" value={passwords.current} onChange={e => setPasswords({ ...passwords, current: e.target.value })} />
              <label>New Password</label>
              <input className="input" type="password" value={passwords.new} onChange={e => setPasswords({ ...passwords, new: e.target.value })} />
              <label>Confirm Password</label>
              <input className="input" type="password" value={passwords.confirm} onChange={e => setPasswords({ ...passwords, confirm: e.target.value })} />
              <button className="btn btn-outline" type="submit" disabled={savingPassword}>
                {savingPassword ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
